import { useTranslation } from "react-i18next"

import type { FairUseVoicePriceRow, FairUseVoicePricesIssue } from "./fair-use-entitlements-utils"

import { Plus, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { fairUseVoicePriceRow } from "./fair-use-entitlements-utils"

type FairUseVoiceRatesProps = {
  rows: FairUseVoicePriceRow[]
  /** Mirrors `PLATFORM_ONLY_CONFIG_FIELDS`: tenants read, the platform writes. */
  canEdit: boolean
  issue: FairUseVoicePricesIssue
  onChange: (rows: FairUseVoicePriceRow[]) => void
}

/** The shared key: one rate for every voice model without its own line. */
const DEFAULT_KEY = "default"

/**
 * What a voice minute costs (#3087, spec R5).
 *
 * Voice is metered per STARTED minute at a rate the platform sets per voice
 * deployment, written to the ledger as module `voice`. A model with no rate
 * meters 0.00 SEK and logs once — it is never blocked — so an empty table is
 * a legitimate, if unbilled, state rather than an error. The `default` key
 * covers every deployment that has no line of its own.
 *
 * Ids are stored lower-cased and the backend refuses two that differ only in
 * case with a 400, so that is caught here rather than left to be a coin flip
 * over which rate wins.
 *
 * Platform-only, for the obvious reason: the rate is an input to the invoice.
 */
export function FairUseVoiceRates({ rows, canEdit, issue, onChange }: FairUseVoiceRatesProps) {
  const { t } = useTranslation()

  const patch = (index: number, next: Partial<FairUseVoicePriceRow>) => {
    onChange(rows.map((row, position) => (position === index ? { ...row, ...next } : row)))
  }

  const header = (
    <div className="space-y-1">
      <Label className="text-sm font-semibold">
        {t("adminDashboard.fairUse.settings.voice.title", "Voice rate")}
      </Label>
      <p className="text-muted-foreground max-w-prose text-xs">
        {t(
          "adminDashboard.fairUse.settings.voice.caption",
          "SEK per started minute, per voice deployment. Use “default” for one rate covering every voice model; a model with no rate is metered at 0 SEK."
        )}
      </p>
    </div>
  )

  if (!canEdit) {
    return (
      <div className="space-y-2" data-testid="fair-use-voice-rates-readonly">
        {header}
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("adminDashboard.fairUse.settings.voice.none", "No voice rate is set.")}
          </p>
        ) : (
          <ul className="space-y-1 text-sm">
            {rows.map((row) => (
              <li key={row.key} className="flex items-baseline gap-2">
                <span className="font-mono text-xs">{row.id}</span>
                <span className="tabular-nums">
                  {t("adminDashboard.fairUse.settings.voice.perMinuteValue", {
                    defaultValue: "{{rate}} SEK / minute",
                    rate: row.rate,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3" data-testid="fair-use-voice-rates">
      {header}

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.fairUse.settings.voice.emptyHint",
            "No rate yet — voice minutes are metered at 0 SEK until one is added."
          )}
        </p>
      ) : null}

      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={row.key} className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor={`fair-use-voice-model-${index}`}>
                {t("adminDashboard.fairUse.settings.voice.modelLabel", "Voice model")}
              </Label>
              <Input
                className="h-9 w-64 font-mono text-xs"
                id={`fair-use-voice-model-${index}`}
                placeholder={DEFAULT_KEY}
                value={row.id}
                onChange={(event) => patch(index, { id: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor={`fair-use-voice-rate-${index}`}>
                {t("adminDashboard.fairUse.settings.voice.rateLabel", "SEK / minute")}
              </Label>
              <Input
                className="h-9 w-28 tabular-nums"
                id={`fair-use-voice-rate-${index}`}
                inputMode="decimal"
                value={row.rate}
                onChange={(event) => patch(index, { rate: event.target.value })}
              />
            </div>
            <Button
              className="h-9"
              size="icon"
              type="button"
              variant="ghost"
              aria-label={t("adminDashboard.fairUse.settings.voice.remove", {
                defaultValue: "Remove the rate for {{model}}",
                model: row.id || DEFAULT_KEY,
              })}
              onClick={() => onChange(rows.filter((_row, position) => position !== index))}
            >
              <Trash2 className="text-destructive size-4" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        size="sm"
        type="button"
        variant="outline"
        onClick={() => onChange([...rows, fairUseVoicePriceRow()])}
      >
        <Plus className="mr-2 size-4" />
        {t("adminDashboard.fairUse.settings.voice.add", "Add a rate")}
      </Button>

      {issue === "id" && (
        <p className="text-destructive text-xs" data-testid="fair-use-voice-id-error">
          {t(
            "adminDashboard.fairUse.settings.voice.idError",
            "Every rate needs a voice model id — use “default” for the shared one."
          )}
        </p>
      )}
      {issue === "duplicate" && (
        <p className="text-destructive text-xs" data-testid="fair-use-voice-duplicate-error">
          {t(
            "adminDashboard.fairUse.settings.voice.duplicateError",
            "Two rates name the same voice model. Ids are compared without case."
          )}
        </p>
      )}
      {issue === "range" && (
        <p className="text-destructive text-xs" data-testid="fair-use-voice-range-error">
          {t(
            "adminDashboard.fairUse.settings.voice.rangeError",
            "A rate is 0 SEK or more, at most 1000, written with a comma or a point (1,50) and no thousands grouping."
          )}
        </p>
      )}
    </div>
  )
}
